import React, { useState, useRef, useEffect } from 'react';
import { FaChevronDown} from 'react-icons/fa';

const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select an option",
  displayKey = "name",
  valueKey = "id",
  required = false,
  disabled = false,
  className = "",
  customDisplay = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isTyping, setIsTyping] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        const displayText = customDisplay ? customDisplay(option) : String(option[displayKey]);
        return displayText.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, displayKey, customDisplay]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setIsTyping(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option display text
  const getSelectedText = () => {
    if (!value) return '';
    const selectedOption = options.find(option => option[valueKey] === value);
    if (!selectedOption) return '';
    return customDisplay ? customDisplay(selectedOption) : selectedOption[displayKey];
  };

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchTerm('');
    setIsTyping(false);
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setIsTyping(true);
      setSearchTerm('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsTyping(true);
    setIsOpen(true);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0) {
        handleSelect(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setIsTyping(false);
    }
  };

  const handleInputBlur = () => {
    // Don't close immediately to allow clicking on options
    setTimeout(() => {
      if (!isOpen) {
        setSearchTerm('');
        setIsTyping(false);
      }
    }, 200);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isTyping ? searchTerm : getSelectedText()}
          onChange={handleInputChange}
          onClick={handleInputClick}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className={`w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none cursor-pointer ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}
          readOnly={disabled}
        />
        <FaChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 shadow-lg max-h-60 overflow-hidden">
          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option[valueKey] || index}
                  className="px-3 py-1.5 text-[13px] hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                  onClick={() => handleSelect(option)}
                >
                  {customDisplay ? customDisplay(option) : option[displayKey]}
                </div>
              ))
            ) : (
              <div className="px-3 py-1.5 text-[13px] text-gray-500 text-center">
                No options found
              </div>
            )}
          </div>
        </div>
      )}

      {required && (
        <input
          type="hidden"
          value={value || ''}
          required
        />
      )}
    </div>
  );
};

export default SearchableDropdown; 